import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InputCheckbox, Button } from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { ActiveCartPromoProduct } from 'types/cart';
import { useFeature } from 'hooks/useFeature';
import { Size } from '__generated__/graphql';
import { getSizesFromSizeGroups } from 'utils/products';
import { resizeProductImage } from 'utils/image';

import { Img } from '../../Img';
import { QuantityDropdown } from '../../QuantityDropdown';
import { Sizes, TileSizes } from '../../Sizes';
import { Styles } from '../../Styles';

import { ItemIdentifiersType, SelectedItemType } from './types';
import { isMatchForSelectedItem } from './utils';

type ProductSelectorProps = {
  addSelectedItem: (i: SelectedItemType) => void;
  excludedColors: string[];
  error?: string;
  initialSwatch?: string;
  maxAllowedQuantity?: number;
  product: ActiveCartPromoProduct;
  removeSelectedItem: (i: ItemIdentifiersType) => void;
  selectedItems: SelectedItemType[];
  updateSelectedItemQuantity: (i: ItemIdentifiersType, q: number) => void;
};

export const GWPOld_ProductSelector = ({
  addSelectedItem,
  excludedColors,
  error,
  initialSwatch,
  maxAllowedQuantity,
  product,
  removeSelectedItem,
  selectedItems,
  updateSelectedItemQuantity,
}: ProductSelectorProps) => {
  const translate = useTranslate();
  const hideEanColorCode = useFeature('HIDE_EAN_COLOR_CODE');

  const { name, variations } = product;
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState<Size | undefined>();
  const [color, setColor] = useState(
    () => product.variations?.[0].colorValue || ''
  );

  const chosenVariant =
    variations &&
    variations.find(variant =>
      color
        ? variant.colorValue === color
        : initialSwatch
        ? variant.colorValue === initialSwatch
        : true
    );

  const addItem = e => {
    e.preventDefault(); // Need this to prevent resetting of color value
    if (size) {
      const giftToAdd: SelectedItemType = {
        color,
        colorName:
          variations.find(v => v.colorValue === color)?.colorName || '',
        id: chosenVariant!.id,
        productName: product.name,
        quantity,
        size,
      };

      addSelectedItem(giftToAdd);
    }
  };

  const removeItem = useCallback(
    () =>
      removeSelectedItem({
        id: chosenVariant!.id,
        color: chosenVariant!.colorValue,
        size: size!,
      }),
    [chosenVariant, size, removeSelectedItem]
  );

  const updateQuantity = useCallback(
    newQuantity => {
      // Update quantity for quantity dropdown
      setQuantity(newQuantity);

      // if there are selected items, update the quantity there
      if (selectedItems.length && chosenVariant) {
        const item: ItemIdentifiersType = {
          id: chosenVariant!.id,
          color: chosenVariant!.colorValue,
          size: size!,
        };
        updateSelectedItemQuantity(item, newQuantity);
      }
    },
    [chosenVariant, selectedItems, size, updateSelectedItemQuantity]
  );

  // Set Default Size
  const sizes = chosenVariant
    ? getSizesFromSizeGroups(chosenVariant.sizeGroups)
    : [];
  const defaultSize = sizes.filter(f => f.orderable)[0];
  useEffect(() => {
    setSize(defaultSize);
  }, [defaultSize]);

  const isVariantSelected = useMemo(() => {
    if (!chosenVariant || !selectedItems.length) return false;

    return (
      selectedItems.filter(selectedItem => {
        const item = {
          id: chosenVariant!.id,
          color: chosenVariant!.colorValue,
          size: size!,
        };
        return isMatchForSelectedItem(item, selectedItem);
      }).length > 0
    );
  }, [chosenVariant, selectedItems, size]);

  // Set the quantity of selected item so it will be reflected in the Quantity
  // Dropdown
  useEffect(() => {
    if (isVariantSelected && chosenVariant) {
      const selectedItem = selectedItems.find(item =>
        isMatchForSelectedItem(
          {
            id: chosenVariant.id,
            color: chosenVariant.colorValue,
            size: size!,
          },
          item
        )
      );
      if (selectedItem) setQuantity(selectedItem?.quantity);
    }
  }, [isVariantSelected, chosenVariant, selectedItems, size]);

  return (
    <article
      data-test-id="gift-purchase-product"
      className="flex relative items-center p-4 border border-4 rounded"
    >
      <div className="grid items-center lg:grid-cols-2 md:grid-cols-2 gap-5">
        <div className="flex items-center bg-puma-black-700">
          <Img
            width={400}
            src={resizeProductImage(chosenVariant?.images[0].href, 800)}
            alt={chosenVariant?.images[0].alt || ''}
          />
        </div>
        <div className="space-y-3">
          {!hideEanColorCode && (
            <span className="text-puma-black-300">#{chosenVariant?.id}</span>
          )}
          <div className="flex flex-col">
            <h1 className="xs:text-xl xl:text-2xl font-bold">{name}</h1>
            <h2 className="text-xs">
              {hideEanColorCode && `${translate('color')}: `}
              {chosenVariant?.colorName}
            </h2>
          </div>
          {variations && (
            <div id="quickview-modal-swatch-selector">
              <Styles
                desiredSwatch={color}
                variations={variations.filter(
                  variant => !excludedColors.includes(variant.colorName!)
                )}
                onChange={newColor => {
                  if (color !== newColor) {
                    setColor(newColor);
                    setSize(undefined);
                  }
                }}
              />
            </div>
          )}
          {chosenVariant && (
            <Sizes
              sizeGroups={chosenVariant.sizeGroups}
              desiredSize={size?.id}
              onChange={newSize => {
                setSize(sizes.find(size => size.id === newSize));
              }}
              tileSize={TileSizes.SMALL}
            />
          )}
          <div className="flex items-center">
            <QuantityDropdown
              quantity={quantity}
              setQuantity={updateQuantity}
              maxQuantity={maxAllowedQuantity}
            />
            {isVariantSelected ? (
              <InputCheckbox
                id="variant-selected"
                checked={isVariantSelected}
                onChange={removeItem}
              >
                <span className="-ml-2">{translate('productSelected')}</span>
              </InputCheckbox>
            ) : (
              <Button
                dataTestId="gwp-old-modal-add-button"
                className="flex-1"
                label={translate('add')}
                variant="primary"
                onClick={addItem}
                loading={false}
              />
            )}
          </div>
          <div>{error && <p>{error}</p>}</div>
        </div>
      </div>
    </article>
  );
};
